import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleLayout from "@/components/feature/ArticleLayout";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfansspreadsheet.help";
const articlePath = "/artykul/jak-korzystac-usfansspreadsheet";
const articleTitle = "Jak korzystać z USFansSpreadsheet – poradnik krok po kroku";
const articleDesc = "Naucz się korzystać z USFansSpreadsheet krok po kroku. Praktyczny poradnik obejmujący rejestrację, tworzenie arkuszy, formuły i zaawansowane funkcje analityczne.";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": articleTitle,
  "description": articleDesc,
  "author": { "@type": "Person", "name": "Anna Kowalska", "jobTitle": "Specjalista SEO" },
  "datePublished": "2026-06-03",
  "dateModified": "2026-06-24",
  "publisher": { "@type": "Organization", "name": "USFansSpreadsheet.help", "url": siteUrl },
  "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}${articlePath}` },
  "inLanguage": "pl-PL"
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": siteUrl },
    { "@type": "ListItem", "position": 2, "name": "Artykuły", "item": `${siteUrl}/artykul/przewodnik-usfansspreadsheet` },
    { "@type": "ListItem", "position": 3, "name": "Poradnik" }
  ]
};

const relatedArticles = [
  { title: "Kompletny przewodnik po USFansSpreadsheet", to: "/artykul/przewodnik-usfansspreadsheet" },
  { title: "USFansSpreadsheet dla początkujących", to: "/artykul/dla-poczatkujacych" },
  { title: "10 praktycznych wskazówek dla USFansSpreadsheet", to: "/artykul/wskazowki-usfansspreadsheet" },
];

export default function ArticleTutorial() {
  return (
    <>
      <PageMeta
        title={articleTitle}
        description={articleDesc}
        keywords="USFansSpreadsheet, poradnik, jak korzystać, krok po kroku, tutorial, arkusz kalkulacyjny, Polska"
        canonicalPath={articlePath}
        ogType="article"
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />

      <Navbar />
      <ArticleLayout
        title="Jak korzystać z USFansSpreadsheet – poradnik krok po kroku"
        category="Poradnik"
        heroImg="https://readdy.ai/api/search-image?query=Hands%20on%20keyboard%20with%20an%20open%20notebook%20showing%20handwritten%20steps%20beside%20a%20laptop%20displaying%20spreadsheet%20tutorial%2C%20warm%20cream%20desk%2C%20soft%20natural%20lighting%2C%20organized%20productive%20workspace%2C%20editorial%20lifestyle%20photography&width=1400&height=600&seq=art-tutorial-hero-v2&orientation=landscape"
        heroImgAlt="Poradnik USFansSpreadsheet"
        relatedArticles={relatedArticles}
      >
        <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
          Rozpoczęcie pracy z <strong>USFansSpreadsheet</strong> jest prostsze niż myślisz. W tym poradniku krok po kroku przeprowadzimy Cię przez wszystkie etapy — od utworzenia konta po wykorzystanie zaawansowanych funkcji analitycznych.
        </p>

        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 pt-4">Krok 1: Rejestracja i konfiguracja konta</h2>
        <p>
          Pierwszym krokiem jest utworzenie konta na platformie <strong>USFansSpreadsheet</strong>. Proces rejestracji zajmuje zaledwie kilka minut. Po podaniu podstawowych danych otrzymasz dostęp do panelu głównego, gdzie możesz od razu rozpocząć pracę.
        </p>

        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 pt-4">Krok 2: Tworzenie pierwszego arkusza</h2>
        <p>
          Po zalogowaniu możesz utworzyć swój pierwszy arkusz kalkulacyjny. <strong>USFansSpreadsheet</strong> oferuje bibliotekę gotowych szablonów, które znacznie przyspieszają pracę — od budżetów domowych po zaawansowane raporty biznesowe.
        </p>

        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 pt-4">Krok 3: Podstawowe funkcje i formuły</h2>
        <p>
          <strong>USFansSpreadsheet</strong> obsługuje wszystkie standardowe formuły arkuszy kalkulacyjnych. Interfejs został zaprojektowany intuicyjnie — wystarczy kliknąć komórkę i rozpocząć wpisywanie formuły, a system podpowie dostępne opcje.
        </p>

        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 pt-4">Krok 4: Zaawansowane funkcje</h2>
        <p>
          Gdy opanujesz podstawy, możesz przejść do zaawansowanych funkcji <strong>USFansSpreadsheet</strong>, takich jak tabele przestawne, wykresy dynamiczne i automatyzacja zadań. Te narzędzia pozwalają na głęboką analizę danych i tworzenie profesjonalnych raportów.
        </p>

        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 pt-4">Podsumowanie</h2>
        <p>
          <strong>USFansSpreadsheet</strong> to potężne narzędzie, które staje się coraz bardziej użyteczne w miarę poznawania jego funkcji. Niezależnie od poziomu zaawansowania, platforma oferuje wszystko, czego potrzebujesz do efektywnej pracy z danymi.
        </p>
      </ArticleLayout>
      <Footer />
    </>
  );
}